import { useEffect, useState } from 'react';
import { api, ApiError, getConfig, type Issue } from '../api';
import { ClaimForm } from '../components/ClaimForm';
import { EMPTY_FORM, SaleForm } from '../components/SaleForm';
import { SignIn } from '../components/SignIn';
import { Footer, TopBar } from '../components/TopBar';
import { Turnstile } from '../components/Turnstile';

interface Created {
  public_id: string;
  status: 'published' | 'flagged';
  listing_url?: string;
  edit_url?: string;
  emailed?: boolean;
}

interface DuplicateHint {
  eventId: string;
  publicId: string;
  title: string;
  claimable: boolean;
}

export function Submit() {
  const [siteKey, setSiteKey] = useState<string | null | undefined>(undefined);
  const [token, setToken] = useState('');
  const [resetKey, setResetKey] = useState(0);
  const [issues, setIssues] = useState<Issue[]>([]);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState<Created | null>(null);
  const [copied, setCopied] = useState(false);
  const [duplicate, setDuplicate] = useState<DuplicateHint | null>(null);
  const [claimSent, setClaimSent] = useState(false);
  const [user, setUser] = useState<{ email: string; display_name: string | null } | null | undefined>(undefined);
  const [pricing, setPricing] = useState<{ price_cents: number; account_type: 'residential' | 'commercial' } | null>(null);
  const [feature, setFeature] = useState(false);

  useEffect(() => {
    document.title = 'List your sale, market or event — free — The Yard Sale Map';
    getConfig()
      .then((c) => setSiteKey(c.turnstileSiteKey))
      .catch(() => setSiteKey(null));
    api
      .get<{ user: { email: string; display_name: string | null } | null }>('/me')
      .then((r) => setUser(r.user))
      .catch(() => setUser(null));
  }, []);

  useEffect(() => {
    if (user) {
      api
        .get<{ price_cents: number; account_type: 'residential' | 'commercial' }>('/submissions/pricing')
        .then(setPricing)
        .catch(() => setPricing(null));
    }
  }, [user]);

  async function submit(fields: Record<string, string>, photos: File[]) {
    setBusy(true);
    setIssues([]);
    setError('');
    setDuplicate(null);
    if (siteKey && !token) {
      setError('Please complete the “verify you are human” check above the button.');
      setBusy(false);
      return;
    }
    const fd = new FormData();
    for (const [k, val] of Object.entries(fields)) fd.append(k, val);
    if (feature) fd.append('feature', 'true');
    fd.append('cf-turnstile-response', token);
    for (const p of photos) fd.append('photos', p, p.name);
    try {
      const r = await api.postForm<Created & { checkout_url?: string }>('/submissions', fd);
      if (r.checkout_url) {
        window.location.href = r.checkout_url;
        return;
      }
      setDone(r);
      window.scrollTo(0, 0);
    } catch (e) {
      if (e instanceof ApiError && e.status === 409 && e.code === 'possible_duplicate') {
        setDuplicate({
          eventId: e.data.event_id as string,
          publicId: e.data.public_id as string,
          title: e.data.title as string,
          claimable: Boolean(e.data.claimable),
        });
        setError(e.message);
        window.scrollTo(0, 0);
      } else if (e instanceof ApiError) {
        setIssues(e.issues);
        setError(e.message);
        if (e.issues[0]) document.getElementById(e.issues[0].path)?.focus();
        setResetKey((k) => k + 1);
      } else {
        setError('Something went wrong sending your sale. Please try again.');
        setResetKey((k) => k + 1);
      }
    } finally {
      setBusy(false);
    }
  }

  if (done) {
    const listing = done.listing_url ? new URL(done.listing_url).pathname : `/e/${done.public_id}`;
    return (
      <>
        <TopBar />
        <main className="page" id="main">
          <h1>Your sale is listed!</h1>
          {done.status === 'flagged' ? (
            <p className="info">We're giving this one a quick look before it appears on the map (usually within a day).</p>
          ) : (
            <p>It's on the map now. Thanks for listing with The Yard Sale Map.</p>
          )}
          <p>
            <a className="btn primary" href={listing}>
              View your listing
            </a>
          </p>
          {done.edit_url && (
            <>
              <h2>Your private edit link</h2>
              <p>
                Use this link to change or cancel your sale — keep it private.{' '}
                {done.emailed ? (
                  'We also emailed it to you.'
                ) : (
                  <strong>Copy it or bookmark it now — this is the only place you’ll see it.</strong>
                )}
              </p>
              <div className="copybox">
                <input readOnly value={done.edit_url} aria-label="Private edit link" onFocus={(e) => e.target.select()} />
                <button
                  type="button"
                  onClick={() => {
                    void navigator.clipboard?.writeText(done.edit_url!).then(() => setCopied(true));
                  }}
                >
                  {copied ? 'Copied' : 'Copy'}
                </button>
              </div>
            </>
          )}
          <p style={{ marginTop: 24 }}>
            <a href="/submit" onClick={() => setDone(null)}>
              List another sale
            </a>
          </p>
        </main>
        <Footer />
      </>
    );
  }

  if (duplicate) {
    const listingHref = `/e/${duplicate.publicId}`;
    return (
      <>
        <TopBar />
        <main className="page" id="main">
          <h1>Is this already listed?</h1>
          {claimSent ? (
            <>
              <p>Thanks — we'll review your request and email you once it's decided.</p>
              <p>
                <a
                  href="/submit"
                  onClick={() => {
                    setDuplicate(null);
                    setClaimSent(false);
                  }}
                >
                  List another sale
                </a>
              </p>
            </>
          ) : duplicate.claimable ? (
            <>
              <p>{error || 'This looks like a sale we already have listed. Is it yours?'}</p>
              <p>
                <a href={listingHref}>{duplicate.title}</a>
              </p>
              <p>If it's your sale, let us know so we can put your contact details on the listing.</p>
              <ClaimForm eventId={duplicate.eventId} onDone={() => setClaimSent(true)} />
              <p style={{ marginTop: 24 }}>
                <a href="/submit" onClick={() => setDuplicate(null)}>
                  Never mind, go back
                </a>
              </p>
            </>
          ) : (
            <>
              <p>{error || "This looks like it's already listed."}</p>
              <p>
                <a className="btn primary" href={listingHref}>
                  View the existing listing
                </a>
              </p>
              <p style={{ marginTop: 24 }}>
                <a href="/submit" onClick={() => setDuplicate(null)}>
                  Go back
                </a>
              </p>
            </>
          )}
        </main>
        <Footer />
      </>
    );
  }

  if (user === undefined) return null;
  if (user === null) {
    return (
      <>
        <TopBar />
        <main className="page" id="main">
          <h1>Sign in to list your sale</h1>
          <p>Free to list. We ask you to sign in so you can manage your listing later, and so featuring a listing (optional) is tied to your account.</p>
          <div className="card" style={{ maxWidth: 420 }}>
            <SignIn />
          </div>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <TopBar />
      <main className="page" id="main">
        <h1>List your sale, market or event — free</h1>
        <p>Most sales appear on the map as soon as you submit.</p>
        <p className="hint">
          Rather email it? Send the date, times, address and a short description to{' '}
          <a href="mailto:addmysale@theyardsalemap.com?subject=Please%20add%20my%20sale">addmysale@theyardsalemap.com</a> and we'll
          add it for you.
        </p>
        {error && (
          <p className="error" role="alert">
            {error}
          </p>
        )}
        <SaleForm
          mode="create"
          initial={user ? { ...EMPTY_FORM, contact_email: user.email } : EMPTY_FORM}
          issues={issues}
          busy={busy || siteKey === undefined}
          onSubmit={(f, p) => void submit(f, p)}
          extra={
            siteKey !== undefined ? (
              <>
                {pricing && (
                  <label className="check">
                    <input type="checkbox" checked={feature} onChange={(e) => setFeature(e.target.checked)} />
                    Feature my listing — ${(pricing.price_cents / 100).toFixed(0)} one-time, moves it to the top of search results for
                    this sale's date. Locks the date/time once paid.
                  </label>
                )}
                <Turnstile siteKey={siteKey} onToken={setToken} resetKey={resetKey} />
              </>
            ) : null
          }
        />
      </main>
      <Footer />
    </>
  );
}
